import { useMemo, useState, ReactNode } from "react";
import * as HoverCard from "@radix-ui/react-hover-card";
import { HoveredClusterInfo, ParsedClusterPoint } from "react-migration/lib/map/layers/ClusteredViewportSearchMapLayer";
import { useDebouncedTooltipInfo } from "./useDebouncedTooltipInfo";

const DEFAULT_TOOLTIP_SHOW_DELAY = 800;
const DEFAULT_TOOLTIP_HIDE_DELAY = 300;
const DEBUG_TOOLTIP_HIT_AREA = false;

export type ClusterTooltipProps<C extends ParsedClusterPoint> = HoverCard.HoverCardContentProps & {
  /** The hovered cluster info from ClusteredViewportSearchMapLayer */
  hoveredClusterInfo: HoveredClusterInfo<C> | null;
  /** The function to render the tooltip content */
  renderTooltipContent: (c: HoveredClusterInfo<C>) => ReactNode;
  /** How long a user needs to hover before showing the tooltip */
  showDelay?: number;
  /** How long the tooltip remains until it is hidden */
  hideDelay?: number;
};

/**
 * Used to create a map tooltip for clusters rendered by a
 * `ClusteredViewportSearchMapLayer` instance.
 */
export function ClusterTooltip<C extends ParsedClusterPoint>({
  hoveredClusterInfo,
  renderTooltipContent,
  showDelay = DEFAULT_TOOLTIP_SHOW_DELAY,
  hideDelay = DEFAULT_TOOLTIP_HIDE_DELAY,
  ...contentPropOverrides
}: ClusterTooltipProps<C>) {
  const [tooltipHovered, setTooltipHovered] = useState(false);

  const clusterToShow = useDebouncedTooltipInfo(hoveredClusterInfo, {
    tooltipHovered,
    showDelay,
    hideDelay,
  });

  const tooltipContent = useMemo(
    () => (clusterToShow ? renderTooltipContent(clusterToShow) : null),
    [clusterToShow, renderTooltipContent]
  );

  if (!tooltipContent) return null;

  return (
    <HoverCard.Root open>
      {clusterToShow && (
        <HoverCard.Trigger
          style={{
            position: "absolute",
            pointerEvents: "none",
            opacity: DEBUG_TOOLTIP_HIT_AREA ? 0.8 : 0,
            top: clusterToShow.bbox[1],
            left: clusterToShow.bbox[0],
            width: clusterToShow.width,
            height: clusterToShow.height,
            background: tooltipHovered ? "green" : "red",
          }}
        />
      )}

      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          sideOffset={8}
          align="start"
          className="bg-white rounded-md drop-shadow-lg border-blue-grey-100 p-1.5 w-[180px] max-h-[250px] flex flex-col"
          onMouseEnter={() => setTooltipHovered(true)}
          onMouseLeave={() => setTooltipHovered(false)}
          {...contentPropOverrides}
        >
          {tooltipContent}
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
